import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom'
import { FormattedMessage, FormattedDate, useIntl } from 'react-intl'
import { useFormik } from 'formik'
import { useSnackbar } from 'notistack'
import { filter, get, intersection, merge, cloneDeep } from 'lodash'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import { useTeam } from '../../../contexts/TeamContext'
import { useNodes } from '../../../contexts/NodesContext'
import { useDomains } from '../../../contexts/DomainsContext'
import { useProjects } from '../../../contexts/ProjectsContext'
import { useDockerNetworks } from '../../../contexts/DockerNetworksContext'
import useTeamPackageImportsResources from '../../../shared/resources/api/v1/admin/packages/useTeamPackageImportsResources'
import useTeamIngressControllersResources from '../../../shared/resources/api/v1/admin/docker/useTeamIngressControllersResources'
import useMarketplacePackagesResources from '../../../shared/resources/api/v1/marketplace/useMarketplacePackagesResources'
import useCreateProjectDeployment from '../../../shared/hooks/useCreateProjectDeployment'
import LoadingState from '../../../shared/components/LoadingState'
import ErrorState from '../../../shared/components/ErrorState'
import PageHeader from '../../../shared/components/layouts/PageHeader'
import PageBody from '../../../shared/components/layouts/PageBody'
import Page from '../../../shared/components/layouts/Page'
import Flex from '../../../shared/components/layouts/Flex'
import FlexColumn from '../../../shared/components/layouts/FlexColumn'
import FormikSchemaFields from '../../../shared/components/formik/FormikSchemaFields'
import PackageImportStatusChip from '../../packages/imports/components/PackageImportStatusChip'
import { buildDefaultValues, buildValidationSchema } from '../../../shared/utils/schemaFormUtils'

function PackageImportShowPage() {
  const intl = useIntl()
  const { formatMessage } = intl
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { teamSlug, packageImportId } = useParams()
  const { team } = useTeam()
  const { nodes, nodesById } = useNodes()
  const { domains } = useDomains()
  const { projectsById } = useProjects()
  const { networks } = useDockerNetworks()
  const { all: getAllIngressControllers } = useTeamIngressControllersResources(teamSlug)
  const { get: getPackageImport, update, import: importAction, edit: getEditData } = useTeamPackageImportsResources(teamSlug)
  const { get: getPackage } = useMarketplacePackagesResources()
  const { createDeployment } = useCreateProjectDeployment(teamSlug)

  const [packageImport, setPackageImport] = useState(null)
  const [editData, setEditData] = useState(null)
  const [packageData, setPackageData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState(null)
  const [teamIngressControllers, setTeamIngressControllers] = useState([])

  const loadData = useCallback((silent = false) => {
    if (!silent) {
      setLoading(true)
    }
    setError(null)

    Promise.all([
      getPackageImport(packageImportId),
      getEditData(packageImportId)
    ])
      .then(([importResponse, editResponse]) => {
        const importData = importResponse.packageImport
        setPackageImport(importData)
        setEditData(editResponse.packageImport)
        return getPackage(importData.packageId)
      })
      .then((response) => {
        setPackageData(response.package)
      })
      .catch((err) => {
        setError(err)
      })
      .finally(() => {
        setLoading(false)
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageImportId])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Load team ingress controllers
  useEffect(() => {
    getAllIngressControllers()
      .then((response) => {
        setTeamIngressControllers(response.ingressControllers || [])
      })
      .catch(() => {
        setTeamIngressControllers([])
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamSlug])

  // Get project from edit data
  const project = useMemo(() => {
    if (!packageImport) return null
    return get(projectsById, packageImport.projectId)
  }, [packageImport, projectsById])

  // Get node from edit data
  const node = useMemo(() => {
    if (!editData) return null
    return get(nodesById, editData.nodeId)
  }, [editData, nodesById])

  const projectNodeIds = useMemo(() => {
    if (!project) return []
    return get(project, 'nodeIds', [])
  }, [project])

  // Filter domains to only those on project's nodes
  const filteredDomains = useMemo(() => {
    return filter(domains, (domain) => projectNodeIds.includes(domain.nodeId))
  }, [domains, projectNodeIds])

  // Filter ingress controllers to those from projects sharing nodes
  const filteredIngressControllers = useMemo(() => {
    return filter(teamIngressControllers, (ic) => {
      const icProject = get(projectsById, ic.projectId)
      const icNodeIds = get(icProject, 'nodeIds', [])
      const sharedNodes = intersection(projectNodeIds, icNodeIds)
      return sharedNodes.length > 0
    })
  }, [teamIngressControllers, projectsById, projectNodeIds])

  // Filter networks to those from projects sharing nodes
  const filteredNetworks = useMemo(() => {
    if (!networks) return []
    return filter(networks, (network) => {
      const networkProject = get(projectsById, network.projectId)
      const networkNodeIds = get(networkProject, 'nodeIds', [])
      const sharedNodes = intersection(projectNodeIds, networkNodeIds)
      return sharedNodes.length > 0
    })
  }, [networks, projectsById, projectNodeIds])

  const schema = useMemo(() => {
    return get(packageData, 'schema', null)
  }, [packageData])

  const initialValues = useMemo(() => {
    const defaults = buildDefaultValues(schema)
    const existingArgs = editData?.args || {}
    return merge(cloneDeep(defaults), cloneDeep(existingArgs))
  }, [schema, editData])

  const validationSchema = useMemo(() => {
    return buildValidationSchema(schema, intl)
  }, [schema, intl])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {}
  })

  const navigateToList = useCallback(() => {
    navigate(`/teams/${teamSlug}/docker/package-imports`)
  }, [navigate, teamSlug])

  // Save handler - does NOT check validation
  const handleSave = useCallback(() => {
    setSaving(true)
    const data = { packageImport: { args: formik.values } }
    update(packageImportId, data)
      .then(() => {
        loadData(true)
        enqueueSnackbar(formatMessage({ id: 'packageImports.show.saveSuccess' }), { variant: 'success' })
      })
      .catch((err) => {
        enqueueSnackbar(err.message || formatMessage({ id: 'packageImports.show.saveError' }), { variant: 'error' })
      })
      .finally(() => {
        setSaving(false)
      })
  }, [packageImportId, formik.values, update, enqueueSnackbar, formatMessage, loadData])

  // Helper to flatten nested errors object into dot-notation touched paths
  const flattenErrorsToTouched = (errors, prefix = '') => {
    return Object.keys(errors).reduce((acc, key) => {
      const path = prefix ? `${prefix}.${key}` : key
      const value = errors[key]

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        return { ...acc, ...flattenErrorsToTouched(value, path) }
      }
      acc[path] = true
      return acc
    }, {})
  }

  // Import handler - validates first, then auto-deploys
  const handleImport = useCallback(() => {
    formik.validateForm().then((errors) => {
      if (Object.keys(errors).length > 0) {
        // Mark all nested field paths as touched to show error states
        const touchedFields = flattenErrorsToTouched(errors)
        formik.setTouched(touchedFields)
        enqueueSnackbar(formatMessage({ id: 'packageImports.show.validationRequired' }), { variant: 'warning' })
        return
      }

      setImporting(true)
      const data = { packageImport: { args: formik.values } }
      update(packageImportId, data)
        .then(() => importAction(packageImportId))
        .then(() => {
          loadData(true)
          enqueueSnackbar(formatMessage({ id: 'packageImports.import.success' }), { variant: 'success' })

          // Auto-deploy if project exists
          if (project) {
            return createDeployment(project)
              .then(() => {
                enqueueSnackbar(formatMessage({ id: 'packageImports.autoDeploy.success' }), { variant: 'success' })
              })
              .catch(() => {
                enqueueSnackbar(formatMessage({ id: 'packageImports.autoDeploy.error' }), { variant: 'warning' })
              })
          }
        })
        .catch((err) => {
          enqueueSnackbar(err.message || formatMessage({ id: 'packageImports.import.error' }), { variant: 'error' })
        })
        .finally(() => {
          setImporting(false)
        })
    })
  }, [packageImportId, formik, update, importAction, enqueueSnackbar, formatMessage, project, createDeployment, loadData])

  if (loading) {
    return <LoadingState />
  }

  if (error) {
    return <ErrorState messageId="packageImports.show.error" />
  }

  return (
    <Page>
      <PageHeader
        title={packageData?.name || formatMessage({ id: 'packageImports.show.title' })}
        entities={{ team }}
        actions={
          <>
            <Button
              color="primary"
              variant="contained"
              onClick={handleImport}
              disabled={saving || importing}
            >
              <FormattedMessage id="packageImports.show.importButton" />
            </Button>
            <Button
              color="primary"
              variant="outlined"
              onClick={handleSave}
              disabled={saving || importing}
            >
              <FormattedMessage id="packageImports.show.saveButton" />
            </Button>
            <Button
              variant="outlined"
              onClick={navigateToList}
              disabled={saving || importing}
            >
              <FormattedMessage id="app.actions.cancel.label" />
            </Button>
          </>
        }
      />
      <PageBody maxWidth="xl">
        {/* Metadata Section */}
        <Box sx={{ mb: 4 }}>
          <Flex gap={1} sx={{ mb: 2 }}>
            <PackageImportStatusChip packageImport={packageImport} />
          </Flex>

          <FlexColumn gap={1}>
            <Flex gap={1}>
              <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="packageImports.show.metadata.project" />:
              </Typography>
              {project ? (
                <Link
                  component={RouterLink}
                  to={`/teams/${teamSlug}/docker/projects/${project.slug}`}
                  variant="body2"
                >
                  {project.name}
                </Link>
              ) : (
                <Typography variant="body2">{editData?.projectName || '-'}</Typography>
              )}
            </Flex>

            <Flex gap={1}>
              <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="packageImports.show.metadata.node" />:
              </Typography>
              <Typography variant="body2">{node?.name || '-'}</Typography>
            </Flex>

            {packageImport.importedAt && (
              <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="packageImports.show.metadata.lastImported" />:{' '}
                <FormattedDate
                  value={packageImport.importedAt}
                  year="numeric"
                  month="short"
                  day="numeric"
                  hour="numeric"
                  minute="numeric"
                />
              </Typography>
            )}

            {packageImport.failedAt && (
              <Typography variant="body2" color="error">
                <FormattedMessage id="packageImports.show.metadata.lastFailed" />:{' '}
                <FormattedDate
                  value={packageImport.failedAt}
                  year="numeric"
                  month="short"
                  day="numeric"
                  hour="numeric"
                  minute="numeric"
                />
                {packageImport.error && ` - ${packageImport.error}`}
              </Typography>
            )}
          </FlexColumn>
        </Box>

        {/* Form Section */}
        <Box sx={{ maxWidth: 1280 }}>
          <Box component="form" key={packageImport?.id}>
            {schema && (
              <FormikSchemaFields
                formik={formik}
                schema={schema}
                nodes={nodes}
                domains={filteredDomains}
                ingressControllers={filteredIngressControllers}
                networks={filteredNetworks}
              />
            )}
          </Box>
        </Box>
      </PageBody>
    </Page>
  )
}

export default PackageImportShowPage
